import {
  SET_GRAPHICS_SECTION,
  SET_GRAPHICS_SECTIONS,
  SET_ALL_GRAPHICS,
  SET_SELFIE_MEDIA,
  SET_GRAPHICS_SELECTED_IND,
  SET_WATERMARKS,
  SET_MULTISELECT_VALUE,
  ADD_GRAPHICS_ITEM,
  DELETE_GRAPHICS_ITEM,
  SET_GRAPHICS_ERROR,
  UPLOAD_GRAPHICS_FILE,
  UPLOAD_GRAPHICS_FILE_DONE,
  SET_CUSTOM_ICON_FILE,
  SET_CUSTOM_ICON_ERROR,
  UPLOAD_CUSTOM_ICON_START,
  UPLOAD_CUSTOM_ICON_DONE,
} from './types';
import arenaImage from '../../../assets/srsIcons/arena-find-me.svg';
import stadiumImage from '../../../assets/srsIcons/stadium-find-me.svg';
import circleZone from '../../../assets/srsIcons/circle-zone.svg';

const initialState = {
  selectedInd: 0,
  sections: [
    {
      key: 'frontPage',
      label: 'Front Image',
      fileFormat: ['.webp', '.svg'],
      error: null,
      isUploading: false,
      file: null,
      validationGuideline: 'At least 720x1280px (9:16 aspect ratio), WebP or SVG, and up to 200 KB.',
      data: {},
    },
    {
      key: 'venueType',
      label: 'SRS Icon',
      options: {
        arena: { label: 'Arena', imageSrc: arenaImage },
        stadium: { label: 'Stadium', imageSrc: stadiumImage },
        zone: { label: 'Zone', imageSrc: circleZone },
        custom: { label: 'Custom', imageSrc: null, isCustom: true },
      },
      value: 'stadium',
      customIconFileFormat: '.svg',
      customIconValidationGuideline: 'SVG format recommended. The icon will be used in the Find Me page.',
      customIconError: null,
      customIconIsUploading: false,
      customIconFile: null,
      customIconData: {},
      validationGuideline: 'SVG format. The icon will be used in the Find Me page.',
    },
    {
      key: 'logo',
      label: 'Email Header',
      fileFormat: ['.jpeg', '.png'],
      error: null,
      isUploading: false,
      file: null,
      validationGuideline: 'At least 600x300px (2:1 or 3:2 aspect ratio preferable), JPEG or PNG, and up to 150 KB.',
      data: {},
    },
    {
      key: 'slideshowBanner',
      label: 'Slideshow Banner',
      fileFormat: ['.jpeg', '.png'],
      error: null,
      isUploading: false,
      file: null,
      validationGuideline: 'Image should be 1920x180px, JPEG or PNG, and up to 150 KB.',
      data: {},
    },
    {
      key: 'shareImage',
      label: 'SEO Image',
      fileFormat: ['.jpeg', '.png'],
      error: null,
      isUploading: false,
      file: null,
      validationGuideline: 'This image would appear on search results on google or other search engines. Image must be squared',
      data: {},
    },
    {
      key: 'watermark',
      label: 'Watermark',
      fileFormat: '.png',
      error: null,
      isUploading: false,
      file: null,
      validationGuideline: 'Be 500x500px, PNG, and up to 50 KB.',
      data: {},
    },
  ],
};

export default (state = initialState, { payload = {}, type } = {}) => {
  switch (type) {
  case SET_GRAPHICS_SECTIONS: {
    return {
      ...state,
      sections: payload,
      selectedInd: 0,
    };
  }

  case SET_SELFIE_MEDIA: {
    const { media = {}, watermarks = [] } = payload;
    return {
      ...state,
      sections: state.sections.map((section) => {
        if (section.key === 'watermarks') {
          return {
            ...section,
            data: { watermarks: Array.isArray(watermarks) ? watermarks : [] },
          };
        }
        if (!section.mediaKey) return section;
        const url = media?.[section.mediaKey] || null;
        return {
          ...section,
          file: null,
          data: url ? { [`${section.key}Url`]: url } : {},
        };
      }),
    };
  }

  case SET_WATERMARKS: {
    const { watermarks } = payload;
    return {
      ...state,
      sections: state.sections.map((section) => {
        if (section.key !== 'watermarks') return section;
        return {
          ...section,
          data: { ...section.data, watermarks: Array.isArray(watermarks) ? watermarks : [] },
        };
      }),
    };
  }

  case SET_ALL_GRAPHICS: {
    return {
      ...state,
      sections: state.sections.map((section) => {
        if (section.key === 'venueType') {
          return {
            ...section,
            value: payload.venueType || section.value,
            customIconData: Object.fromEntries(
              Object.entries(payload).filter(([entryKey]) => entryKey.startsWith('srsIcon')),
            ),
          };
        }
        return {
          ...section,
          data: !section.value && Object.fromEntries(
            Object.entries(payload).filter(([entryKey]) => entryKey.startsWith(section.key)),
          ),
          value: section.value && payload[section.key],
        };
      }),
    };
  }

  case SET_GRAPHICS_SELECTED_IND: {
    const { selectedInd } = payload;

    return {
      ...state,
      selectedInd,
    };
  }

  case SET_GRAPHICS_SECTION: {
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        payload,
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case SET_MULTISELECT_VALUE: {
    const { value } = payload;

    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        { ...state.sections[state.selectedInd], value },
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case DELETE_GRAPHICS_ITEM: {
    const updatedItem = { ...state.sections[state.selectedInd] };

    if (updatedItem.data) {
      updatedItem.error = null;
      updatedItem.file = null;
      Object.keys(updatedItem.data).forEach((prop) => {
        updatedItem.data[prop] = null;
      });
    } else {
      updatedItem.value = null;
    }

    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        updatedItem,
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case ADD_GRAPHICS_ITEM: {
    const { file = null, data } = payload;

    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        {
          ...state.sections[state.selectedInd],
          file,
          data: data || state.sections[state.selectedInd].data,
          isUploading: file && !data,
        },
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case SET_GRAPHICS_ERROR: {
    const { error } = payload;

    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        {
          ...state.sections[state.selectedInd],
          error,
          file: null,
          isUploading: false,
        },
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case UPLOAD_GRAPHICS_FILE: {
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        {
          ...state.sections[state.selectedInd],
          isUploading: true,
        },
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case UPLOAD_GRAPHICS_FILE_DONE: {
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, state.selectedInd),
        {
          ...state.sections[state.selectedInd],
          isUploading: false,
        },
        ...state.sections.slice(state.selectedInd + 1),
      ],
    };
  }

  case SET_CUSTOM_ICON_FILE: {
    const { file, data } = payload;
    const venueTypeIndex = state.sections.findIndex((s) => s.key === 'venueType');
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, venueTypeIndex),
        {
          ...state.sections[venueTypeIndex],
          customIconFile: file,
          customIconData: data || state.sections[venueTypeIndex].customIconData,
          customIconIsUploading: file && !data,
        },
        ...state.sections.slice(venueTypeIndex + 1),
      ],
    };
  }

  case SET_CUSTOM_ICON_ERROR: {
    const { error } = payload;
    const venueTypeIndex = state.sections.findIndex((s) => s.key === 'venueType');
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, venueTypeIndex),
        {
          ...state.sections[venueTypeIndex],
          customIconError: error,
          customIconFile: null,
          customIconIsUploading: false,
        },
        ...state.sections.slice(venueTypeIndex + 1),
      ],
    };
  }

  case UPLOAD_CUSTOM_ICON_START: {
    const venueTypeIndex = state.sections.findIndex((s) => s.key === 'venueType');
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, venueTypeIndex),
        {
          ...state.sections[venueTypeIndex],
          customIconIsUploading: true,
        },
        ...state.sections.slice(venueTypeIndex + 1),
      ],
    };
  }

  case UPLOAD_CUSTOM_ICON_DONE: {
    const venueTypeIndex = state.sections.findIndex((s) => s.key === 'venueType');
    return {
      ...state,
      sections: [
        ...state.sections.slice(0, venueTypeIndex),
        {
          ...state.sections[venueTypeIndex],
          customIconIsUploading: false,
        },
        ...state.sections.slice(venueTypeIndex + 1),
      ],
    };
  }

  default: {
    return state;
  }
  }
};
